import { useState, useEffect, useCallback, useRef } from 'react';
import { logger } from '../lib/utils/logger';

export interface SessionTimeoutConfig {
  /** Total session timeout in milliseconds (default: 30 minutes) */
  timeoutMs?: number;
  /** Warning time before timeout in milliseconds (default: 5 minutes) */
  warningMs?: number;
  /** Whether to track user activity (default: true) */
  trackActivity?: boolean;
  /** Debounce time for activity tracking in milliseconds (default: 1000) */
  activityDebounceMs?: number;
}

export interface SessionTimeoutState {
  /** Whether the warning modal should be shown */
  showWarning: boolean;
  /** Seconds remaining until timeout */
  secondsRemaining: number;
  /** Function to reset the timeout timer */
  resetTimeout: () => void;
  /** Function to dismiss the warning (also resets timeout) */
  dismissWarning: () => void;
}

// Per-domain idle-timeout config, shared so the two auth providers reference
// named constants instead of inline arithmetic (CU-868kykuzk). Admin is tighter
// than user by design (privileged surface).
export const USER_SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
export const USER_SESSION_WARNING_MS = 5 * 60 * 1000; // 5 minutes
export const ADMIN_SESSION_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
export const ADMIN_SESSION_WARNING_MS = 2 * 60 * 1000; // 2 minutes

const DEFAULT_TIMEOUT_MS = USER_SESSION_TIMEOUT_MS;
const DEFAULT_WARNING_MS = USER_SESSION_WARNING_MS;
const DEFAULT_ACTIVITY_DEBOUNCE_MS = 1000; // 1 second

/**
 * Hook to manage session timeout warnings.
 *
 * Tracks user activity and shows a warning modal before the session expires.
 * This is a frontend-only implementation that assumes a standard session timeout.
 *
 * @param config - Configuration options
 * @param onTimeout - Callback when session expires (e.g., logout)
 * @returns Session timeout state and controls
 *
 * @example
 * ```tsx
 * const { showWarning, secondsRemaining, dismissWarning } = useSessionTimeout(
 *   { timeoutMs: 30 * 60 * 1000, warningMs: 5 * 60 * 1000 },
 *   () => authService.logout()
 * );
 * ```
 */
export function useSessionTimeout(
  config: SessionTimeoutConfig = {},
  onTimeout?: () => void,
): SessionTimeoutState {
  const {
    timeoutMs = DEFAULT_TIMEOUT_MS,
    warningMs = DEFAULT_WARNING_MS,
    trackActivity = true,
    activityDebounceMs = DEFAULT_ACTIVITY_DEBOUNCE_MS,
  } = config;

  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activityDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (activityDebounceRef.current) {
      clearTimeout(activityDebounceRef.current);
      activityDebounceRef.current = null;
    }
  }, []);

  const resetTimeout = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    lastActivityRef.current = Date.now();

    // Set warning timer
    warningTimeoutRef.current = setTimeout(() => {
      logger.info('Session timeout warning triggered');
      setShowWarning(true);

      // Start countdown
      const warningTimeSeconds = Math.floor(warningMs / 1000);
      setSecondsRemaining(warningTimeSeconds);

      countdownIntervalRef.current = setInterval(() => {
        setSecondsRemaining((prev) => {
          if (prev <= 1) {
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, timeoutMs - warningMs);

    // Set timeout timer
    timeoutRef.current = setTimeout(() => {
      logger.warn('Session timeout reached - triggering logout');
      clearAllTimers();
      setShowWarning(false);
      onTimeout?.();
    }, timeoutMs);
  }, [clearAllTimers, timeoutMs, warningMs, onTimeout]);

  const dismissWarning = useCallback(() => {
    logger.info('Session timeout warning dismissed - resetting timer');
    resetTimeout();
  }, [resetTimeout]);

  const handleActivity = useCallback(() => {
    if (!trackActivity) return;

    // Debounce activity tracking
    if (activityDebounceRef.current) {
      clearTimeout(activityDebounceRef.current);
    }

    activityDebounceRef.current = setTimeout(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityRef.current;

      // Only reset if there was actual activity and we're not already showing warning
      if (timeSinceLastActivity > activityDebounceMs && !showWarning) {
        logger.debug('User activity detected - resetting session timeout');
        resetTimeout();
      }
    }, activityDebounceMs);
  }, [trackActivity, activityDebounceMs, showWarning, resetTimeout]);

  // Initialize timeout on mount
  useEffect(() => {
    resetTimeout();
    return () => clearAllTimers();
  }, [resetTimeout, clearAllTimers]);

  // Track user activity
  useEffect(() => {
    if (!trackActivity) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [trackActivity, handleActivity]);

  return {
    showWarning,
    secondsRemaining,
    resetTimeout,
    dismissWarning,
  };
}
